import React, { useState, useEffect, useRef } from 'react';
import MealCard from './MealCard';
import { Checkbox, Label } from './checkbox';
import HelpModal from './HelpModal';
import { SettingsModal } from '../SettingsModal';

const PROTEIN_OPTIONS = [
  { id: 'chicken', label: 'Chicken' },
  { id: 'beef', label: 'Beef' },
  { id: 'pork', label: 'Pork' },
  { id: 'turkey', label: 'Turkey' },
  { id: 'shrimp', label: 'Shrimp' },
  { id: 'white fish', label: 'White Fish' },
  { id: 'meatless', label: 'Meatless' },
  { id: 'salmon', label: 'Salmon' },
  { id: 'tuna', label: 'Tuna' },
];

const MobileView = ({ 
  mealData, 
  filterMeals, 
  likedMeals, 
  handleLikeMeal,
  searchQuery,
  setSearchQuery,
  proteinFilters,
  handleProteinFilterChange,
  handleToggleAllProteins,
  maxPrepTime,
  setMaxPrepTime,
  menuTypeFilters,
  handleMenuTypeFilterChange,
  handleSubmitMeals,
  showSettingsModal,
  setShowSettingsModal,
  userInfo,
  handleSettingsUpdate,
  onSignOut
}) => {
  const [activeMealType, setActiveMealType] = useState('breakfast');
  const [currentPage, setCurrentPage] = useState(1);
  const [showLikedMeals, setShowLikedMeals] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [likedMealFilter, setLikedMealFilter] = useState('all'); // 'all', 'breakfast', 'lunch', 'dinner'
  const [showHelpModal, setShowHelpModal] = useState(false);
  const navRef = useRef(null);
  const mealsPerPage = 20;

  // Track scroll position to make navigation sticky only after scrolling past main nav
  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        const navBottom = navRef.current.getBoundingClientRect().bottom;
        setIsSticky(navBottom <= 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Get meals based on current view mode and filters
  const getMealsToDisplay = () => {
    if (showLikedMeals) {
      // Filter liked meals based on likedMealFilter
      let filteredLikedMeals = likedMeals;
      if (likedMealFilter !== 'all') {
        filteredLikedMeals = likedMeals.filter(meal => meal.type === likedMealFilter);
      }
      return filteredLikedMeals;
    } else {
      // Regular meal browsing
      return filterMeals(mealData[activeMealType] || []);
    }
  };

  // Get the filtered meals based on current view
  const filteredMeals = getMealsToDisplay();
  
  // Sort meals: Seasonal first, then classic, then essential (alphabetically within each)
  const sortedMeals = [...filteredMeals].sort((a, b) => {
    const seasonA = a.season?.toLowerCase() || '';
    const seasonB = b.season?.toLowerCase() || '';
    
    const isSeasonalA = seasonA.includes('spring') || seasonA.includes('summer') || 
                         seasonA.includes('fall') || seasonA.includes('winter');
    const isSeasonalB = seasonB.includes('spring') || seasonB.includes('summer') || 
                         seasonB.includes('fall') || seasonB.includes('winter');
    const isClassicA = seasonA.includes('classic');
    const isClassicB = seasonB.includes('classic');
    const isEssentialA = seasonA.includes('essential');
    const isEssentialB = seasonB.includes('essential');
    
    // Order: 1. Seasonal, 2. Classic, 3. Essential
    if (isSeasonalA && !isSeasonalB) return -1;
    if (!isSeasonalA && isSeasonalB) return 1;
    if (isEssentialA && !isEssentialB) return -1;
    if (!isEssentialA && isEssentialB) return 1;
    if (isClassicA && !isClassicB) return -1;
    if (!isClassicA && isClassicB) return 1;
    
    // If in same category, sort alphabetically
    return a.name.localeCompare(b.name);
  });
  
  // Calculate pagination
  const totalPages = Math.ceil(sortedMeals.length / mealsPerPage);
  const paginatedMeals = sortedMeals.slice(
    (currentPage - 1) * mealsPerPage, 
    currentPage * mealsPerPage
  );

  // Handle meal type navigation
  const handleMealTypeChange = (type) => {
    if (showLikedMeals) {
      // In liked view, this changes the filter
      setLikedMealFilter(type);
    } else {
      // In regular view, this changes the active meal type
      setActiveMealType(type);
    }
    setCurrentPage(1);
  };

  // Handle pagination
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const toggleLikedMeals = () => {
    setShowLikedMeals(!showLikedMeals);
    // Reset to showing all liked meals when toggling to liked view
    if (!showLikedMeals) {
      setLikedMealFilter('all');
    }
    setCurrentPage(1);
  };

  // Count liked meals by type
  const likedBreakfast = likedMeals.filter(meal => meal.type === 'breakfast').length;
  const likedLunch = likedMeals.filter(meal => meal.type === 'lunch').length;
  const likedDinner = likedMeals.filter(meal => meal.type === 'dinner').length;
  const totalLiked = likedBreakfast + likedLunch + likedDinner;

  const toggleHelpModal = () => {
    setShowHelpModal(!showHelpModal);
  };

  return (
    <div className="lg:hidden" ref={navRef}>
      {/* Positioning reference element for scroll detection */}
      <div ref={navRef} className="h-0"></div>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        userInfo={userInfo}
        onUpdate={handleSettingsUpdate}
        onSignOut={onSignOut}
      />

      {/* Mobile Navigation - becomes sticky after scrolling past main nav */}
      <div className={`${isSticky ? 'fixed top-0 left-0 right-0 z-10' : ''} bg-gray-50 pt-2 pb-3 px-4 shadow-sm transition-all duration-200`}>
        {/* Add Back to Selection button at top when in favorites view */}
        {showLikedMeals && (
          <div className="mb-3">
            <button
              onClick={toggleLikedMeals}
              className="w-full bg-blue-100 text-blue-800 rounded-lg py-2 font-medium flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
              </svg>
              Back to Meal Selection
            </button>
          </div>
        )}

        {/* Utility buttons - Update Info and Help on left, Submit on right */}
        <div className="mb-3 flex justify-between">
          {/* Left side buttons */}
          <div className="flex space-x-2">
            <button
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center text-sm bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Update
            </button>
            <button
              onClick={toggleHelpModal}
              className="flex items-center text-sm bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-lg hover:bg-yellow-200 transition-colors"
            >
            
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Help
            </button>
          </div>
          
          {/* Right side - Submit button with paper airplane icon */}
          <button
            onClick={() => {
              // Count meals by type
              const breakfastCount = likedMeals.filter(meal => meal.type === 'breakfast').length;
              const lunchCount = likedMeals.filter(meal => meal.type === 'lunch').length;
              const dinnerCount = likedMeals.filter(meal => meal.type === 'dinner').length;
              
              // Check if we have enough of each meal type
              if (breakfastCount < 8 || lunchCount < 8 || dinnerCount < 14) {
                alert(`Please select enough meals:
                \nBreakfast: ${breakfastCount}/8
                \nLunch: ${lunchCount}/8
                \nDinner: ${dinnerCount}/14`);
                return;
              }
              
              // Handle submission
              if (typeof handleSubmitMeals === 'function') {
                handleSubmitMeals();
              } else {
                alert('Your meal selections have been submitted!');
              }
            }}
            className="flex items-center text-sm bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700 transition-colors"
          >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            <span className="mr-1">Submit</span>
            
          </button>
        </div>

        {/* Meal Type Navigation */}
        <div className="flex items-center justify-center mb-3 bg-white px-4 py-3 rounded-lg shadow-sm">
          {!showLikedMeals ? (
            // Regular meal browsing tabs - equally sized and centered
            <div className="flex w-full justify-center space-x-4">
              <button
                onClick={() => handleMealTypeChange('breakfast')}
                className={`w-1/4 px-2 py-2 text-sm rounded-md font-medium relative ${
                  activeMealType === 'breakfast' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'text-gray-600 bg-gray-100'
                }`}
              >
                Breakfast
                {likedBreakfast > 0 && (
                  <span className="absolute -top-1 -right-1 bg-pink-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {likedBreakfast}
                  </span>
                )}
              </button>
              <button
                onClick={() => handleMealTypeChange('lunch')}
                className={`w-1/4 px-2 py-2 text-sm rounded-md font-medium relative ${
                  activeMealType === 'lunch' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'text-gray-600 bg-gray-100'
                }`}
              >
                Lunch
                {likedLunch > 0 && (
                  <span className="absolute -top-1 -right-1 bg-pink-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {likedLunch}
                  </span>
                )}
              </button>
              <button
                onClick={() => handleMealTypeChange('dinner')}
                className={`w-1/4 px-2 py-2 text-sm rounded-md font-medium relative ${
                  activeMealType === 'dinner' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'text-gray-600 bg-gray-100'
                }`}
              >
                Dinner
                {likedDinner > 0 && (
                  <span className="absolute -top-1 -right-1 bg-pink-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {likedDinner}
                  </span>
                )}
              </button>
            </div>
          ) : (
            // Liked meals filtering tabs - equally sized and centered
            <div className="flex w-full justify-center space-x-2">
              <button
                onClick={() => handleMealTypeChange('all')}
                className={`w-1/5 px-2 py-2 text-xs rounded-md font-medium ${
                  likedMealFilter === 'all' 
                    ? 'bg-pink-100 text-pink-800' 
                    : 'text-gray-600 bg-gray-100'
                }`}
              >
                All ({totalLiked})
              </button>
              <button
                onClick={() => handleMealTypeChange('breakfast')}
                className={`w-1/5 px-2 py-2 text-xs rounded-md font-medium ${
                  likedMealFilter === 'breakfast' 
                    ? 'bg-pink-100 text-pink-800' 
                    : 'text-gray-600 bg-gray-100'
                }`}
              >
                Bfast ({likedBreakfast})
              </button>
              <button
                onClick={() => handleMealTypeChange('lunch')}
                className={`w-1/5 px-2 py-2 text-xs rounded-md font-medium ${
                  likedMealFilter === 'lunch' 
                    ? 'bg-pink-100 text-pink-800' 
                    : 'text-gray-600 bg-gray-100'
                }`}
              >
                Lunch ({likedLunch})
              </button>
              <button
                onClick={() => handleMealTypeChange('dinner')}
                className={`w-1/5 px-2 py-2 text-xs rounded-md font-medium ${
                  likedMealFilter === 'dinner' 
                    ? 'bg-pink-100 text-pink-800' 
                    : 'text-gray-600 bg-gray-100'
                }`}
              >
                Dinner ({likedDinner})
              </button>
            </div>
          )}
        </div>

        {/* Action Buttons Row */}
        <div className="flex space-x-2">
          {/* Liked Meals Toggle Button */}
          <button
            onClick={toggleLikedMeals}
            className={`flex-1 p-2.5 rounded-lg flex items-center justify-between ${
              showLikedMeals ? 'bg-pink-100 text-pink-700' : 'bg-white text-gray-700'
            } shadow-sm`}
          >
            <span className="font-medium">
              {showLikedMeals ? 'Favorites' : 'View Favorites'}
            </span>
            <div className="flex items-center">
              <span className="bg-pink-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mr-1.5">
                {totalLiked}
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
          </button>

          {/* Filter Toggle Button */}
          {!showLikedMeals && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2.5 rounded-lg flex items-center bg-white shadow-sm hover:bg-gray-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="ml-1 font-medium text-gray-700">
                Filters
              </span>
            </button>
          )}
          
          {/* Reset All Button - only show in liked meals view */}
          {showLikedMeals && (
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to remove all liked meals? This cannot be undone.')) {
                  handleLikeMeal(null, true); // Pass true as second argument to indicate reset all
                }
              }}
              className="p-2.5 rounded-lg flex items-center bg-red-100 text-red-700 shadow-sm hover:bg-red-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="ml-1 font-medium">
                Reset All
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Content with padding only when sticky to account for the fixed header */}
      <div className={isSticky ? "pt-24" : "pt-4"}>
        {/* Mobile Filters - only show in regular browsing mode */}
        {!showLikedMeals && showFilters && (
          <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
            <h3 className="font-medium text-lg mb-4">Filter Options</h3>
            
            {/* Search Bar for mobile */}
            <div className="mb-5">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search meals..."
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
                <svg
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Protein Type */}
            <div className="mb-5">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium">Protein Type</h4>
                <button
                  onClick={handleToggleAllProteins}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {proteinFilters.length === PROTEIN_OPTIONS.length ? 'Unselect all' : 'Select all'}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {PROTEIN_OPTIONS.map((option) => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`mobile-${option.id}`}
                      checked={proteinFilters.includes(option.id)}
                      onCheckedChange={() => handleProteinFilterChange(option.id)}
                    />
                    <Label htmlFor={`mobile-${option.id}`} className="text-sm">{option.label}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Prep Time Slider */}
            <div className="mb-5">
              <h4 className="font-medium mb-3">Maximum Prep Time</h4>
              <div className="space-y-3">
                <div className="relative">
                  <input
                    type="range"
                    min="10"
                    max="90"
                    step="5"
                    value={maxPrepTime}
                    onChange={(e) => setMaxPrepTime(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(maxPrepTime - 10) / 80 * 100}%, #e5e7eb ${(maxPrepTime - 10) / 80 * 100}%, #e5e7eb 100%)`
                    }}
                  />
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <div className="space-x-1">
                    <span className="font-medium">{maxPrepTime}</span>
                    <span>minutes or less</span>
                  </div>
                  <div className="flex space-x-2">
                    {[30, 45, 60, 90].map(time => (
                      <button 
                        key={time}
                        onClick={() => setMaxPrepTime(time)}
                        className={`px-2 py-0.5 rounded text-xs ${maxPrepTime === time ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 hover:bg-gray-200'}`}
                      >
                        {time === 90 ? '1.5hr' : time === 60 ? '1hr' : `${time}min`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Types */}
            <div>
              <h4 className="font-medium mb-3">Menu Types</h4>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'classic', label: 'Classics' },
                  { id: 'essential', label: 'Popular' },
                  { id: 'seasonal', label: 'Seasonal' }
                ].map(option => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`mobile-${option.id}`}
                      checked={menuTypeFilters.includes(option.id)}
                      onCheckedChange={() => handleMenuTypeFilterChange(option.id)}
                    />
                    <Label htmlFor={`mobile-${option.id}`} className="text-sm">{option.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Meal Type Header */}
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-xl font-bold capitalize">
            {showLikedMeals 
              ? (likedMealFilter === 'all' 
                ? 'Your Favorites' 
                : `Favorite ${likedMealFilter} Meals`)
              : activeMealType}
          </h2>
          {totalPages > 0 && (
            <div className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </div>
          )}
        </div>

        {/* Meals Display */}
        {paginatedMeals.length > 0 ? (
          <div className={`${showLikedMeals ? 'bg-pink-50 p-4 rounded-lg' : ''}`}>
            <div className="grid grid-cols-2 gap-3">
              {paginatedMeals.map((meal) => (
                <MealCard 
                  key={meal.id}
                  meal={meal}
                  isLiked={likedMeals.some(m => m.id === meal.id)}
                  onLike={handleLikeMeal}
                  compact={true}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className={`bg-white p-6 rounded-lg shadow-sm text-center ${showLikedMeals ? 'bg-pink-50' : ''}`}>
            <p className="text-gray-500">
              {showLikedMeals 
                ? likedMealFilter === 'all'
                  ? 'No liked meals yet' 
                  : `No liked ${likedMealFilter} meals yet`
                : `No ${activeMealType} meals matching your filters`}
            </p>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6 bg-white p-3 rounded-lg shadow-sm">
            <button 
              onClick={goToPrevPage} 
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded ${
                currentPage === 1 
                  ? 'bg-gray-100 text-gray-400' 
                  : 'bg-blue-600 text-white'
              }`}
            >
              Previous
            </button>
            <div className="text-sm">
              {currentPage} / {totalPages}
            </div>
            <button 
              onClick={goToNextPage} 
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded ${
                currentPage === totalPages 
                  ? 'bg-gray-100 text-gray-400' 
                  : 'bg-blue-600 text-white'
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Help Modal */}
      <HelpModal isOpen={showHelpModal} onClose={toggleHelpModal} />
    </div>
  );
};

export default MobileView; 